import { AlertTriangle, CheckCircle, Settings } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { DEPLOYMENT_TYPE_LABELS, type VelociraptorConfig } from '../../types/velociraptor-settings'
import { SectionCard } from './SectionCard'

interface CredentialsStatusProps {
  hasCredentials: boolean
}

function CredentialsStatus({ hasCredentials }: CredentialsStatusProps): React.ReactElement {
  const { t } = useTranslation('admin')
  if (hasCredentials) {
    return (
      <span className="flex items-center gap-1 text-green-600 dark:text-green-400">
        <CheckCircle className="h-4 w-4" />
        {t('velociraptorSettings.configured')}
      </span>
    )
  }
  return (
    <span className="flex items-center gap-1 text-yellow-600 dark:text-yellow-400">
      <AlertTriangle className="h-4 w-4" />
      {t('velociraptorSettings.notSet')}
    </span>
  )
}

interface ConfigGridProps {
  config: VelociraptorConfig
}

function ConfigGrid({ config }: ConfigGridProps): React.ReactElement {
  const { t } = useTranslation('admin')
  const deploymentLabel =
    (DEPLOYMENT_TYPE_LABELS as Record<string, string | undefined>)[config.deploymentType] ??
    config.deploymentType
  return (
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
      <div>
        <span className="text-xs text-stone-500 dark:text-stone-400">
          {t('velociraptorSettings.install.deploymentType')}
        </span>
        <p className="mt-1 text-sm text-stone-900 dark:text-stone-100">{deploymentLabel}</p>
      </div>
      <div>
        <span className="text-xs text-stone-500 dark:text-stone-400">
          {t('velociraptorSettings.apiHost')}
        </span>
        <p className="mt-1 font-mono text-sm text-stone-900 dark:text-stone-100">
          {config.apiHost}:{String(config.apiPort)}
        </p>
      </div>
      <div>
        <span className="text-xs text-stone-500 dark:text-stone-400">
          {t('velociraptorSettings.install.guiPort')}
        </span>
        <p className="mt-1 font-mono text-sm text-stone-900 dark:text-stone-100">
          {String(config.guiPort)}
        </p>
      </div>
      <div>
        <span className="text-xs text-stone-500 dark:text-stone-400">
          {t('velociraptorSettings.credentials')}
        </span>
        <p className="mt-1 text-sm text-stone-900 dark:text-stone-100">
          <CredentialsStatus hasCredentials={config.hasCredentials} />
        </p>
      </div>
    </div>
  )
}

interface ConfigurationCardProps {
  config: VelociraptorConfig | null
}

export function ConfigurationCard({ config }: ConfigurationCardProps): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <SectionCard
      title={t('velociraptorSettings.configuration')}
      description={t('velociraptorSettings.configurationDesc')}
      icon={Settings}
    >
      {config && (
        <div className="space-y-4">
          <ConfigGrid config={config} />
        </div>
      )}
    </SectionCard>
  )
}
